import { useState, useEffect } from "react";
import { Scissors } from "lucide-react";
import SMIT_LOGO from "../assets/smitLogo";

/**
 * SplashScreen — Garment ERP (demo build).
 *
 * Full-screen branded overlay shown on initial app load. Lifecycle:
 *   • mounts visible
 *   • after `holdMs` (1500 by default) starts a `fadeMs` fade-out
 *   • after `holdMs + fadeMs` calls onFinished() so App can unmount it
 *
 * The component owns its own visibility timer. Mounting it = showing it;
 * removing it = it's gone. Parent only needs to handle the onFinished
 * callback to swap state ("showing splash" → "showing app").
 *
 * Centre: the PRODUCT (Garment ERP). Bottom: the DEVELOPER credit (SMIT).
 */

// Brand gradient sampled from the SMIT artwork (orange top → crimson bottom).
const BRAND_FROM = "#F0590F";
const BRAND_TO = "#FC173F";

export default function SplashScreen({
    onFinished,
    holdMs = 1500,
    fadeMs = 400,
}) {
    const [fading, setFading] = useState(false);

    useEffect(() => {
        const startFade = setTimeout(() => setFading(true), holdMs);
        const unmount = setTimeout(() => onFinished?.(), holdMs + fadeMs);
        return () => { clearTimeout(startFade); clearTimeout(unmount); };
    }, [holdMs, fadeMs, onFinished]);

    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 99999,
            background: "linear-gradient(135deg, #FFFFFF 0%, #FAFAFA 100%)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 28, padding: 24,
            opacity: fading ? 0 : 1,
            transition: `opacity ${fadeMs}ms ease-out`,
            pointerEvents: fading ? "none" : "auto",
        }}>
            {/* Product mark with a soft pulsing halo */}
            <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{
                    position: "absolute", width: 340, height: 340, borderRadius: "50%",
                    background: "radial-gradient(closest-side, rgba(252,23,63,0.12), transparent)",
                    animation: "splash-pulse 2s ease-in-out infinite",
                }} />
                <div style={{
                    position: "relative",
                    width: 104, height: 104, borderRadius: 28,
                    background: `linear-gradient(135deg, ${BRAND_FROM} 0%, ${BRAND_TO} 100%)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 18px 40px rgba(252,23,63,0.30)",
                    animation: "splash-rise 700ms cubic-bezier(0.16, 1, 0.3, 1) both",
                }}>
                    <Scissors size={50} style={{ color: "#fff" }} />
                </div>
            </div>

            {/* System name */}
            <div style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                animation: "splash-rise 700ms 250ms cubic-bezier(0.16, 1, 0.3, 1) both",
            }}>
                <div style={{
                    fontSize: 26, fontWeight: 800, letterSpacing: "0.18em",
                    color: "#1F2937", textTransform: "uppercase",
                }}>
                    Garment ERP
                </div>
                <div style={{
                    fontSize: 11, fontWeight: 600, letterSpacing: "0.32em",
                    color: "#9CA3AF", textTransform: "uppercase",
                }}>
                    Production Management Suite
                </div>
            </div>

            {/* Progress dots */}
            <div style={{
                display: "flex", gap: 8, marginTop: 4,
                animation: "splash-rise 700ms 450ms cubic-bezier(0.16, 1, 0.3, 1) both",
            }}>
                {[0, 1, 2].map(i => (
                    <span key={i} style={{
                        width: 8, height: 8, borderRadius: "50%",
                        background: BRAND_TO,
                        animation: `splash-dot 1.2s ${i * 0.18}s ease-in-out infinite`,
                    }} />
                ))}
            </div>

            {/* Developer credit — bottom-fixed */}
            <div style={{
                position: "absolute", bottom: 28, left: 0, right: 0,
                display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
                animation: "splash-rise 700ms 650ms cubic-bezier(0.16, 1, 0.3, 1) both",
            }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", letterSpacing: "0.04em" }}>
                    Developed by
                </div>
                <img src={SMIT_LOGO} alt="SMIT" style={{ height: 40, width: "auto", display: "block" }} />
                <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.04em" }}>
                    Solapurmall Information Technology Pvt. Ltd.
                </div>
            </div>

            {/* Keyframes — scoped inline so the component is fully self-contained
          and doesn't depend on the index.css file the user already maintains */}
            <style>{`
        @keyframes splash-pulse {
          0%, 100% { transform: scale(1); opacity: 0.8; }
          50%      { transform: scale(1.08); opacity: 1; }
        }
        @keyframes splash-rise {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes splash-dot {
          0%, 80%, 100% { transform: scale(0.8); opacity: 0.4; }
          40%           { transform: scale(1.0); opacity: 1.0; }
        }
      `}</style>
        </div>
    );
}
